import { useCallback, useEffect, useState } from 'react';
import {
  ASSET_PAGE_SIZE_DEFAULT,
  ASSET_PAGE_SIZE_OPTIONS,
  ASSET_PAGE_SIZE_STORAGE_KEY,
  AssetDto,
  AssetPackDto,
  AssetSearchMatch,
  AssetSortBy,
  BulkDeleteResult,
  SearchAssetsParams,
  bulkDeleteAssets,
  deleteAsset,
  getAssetPacks,
  getAvailableAssetTypes,
  getAvailableTags,
  searchAssets
} from '../../services/adminAssets';
import { IconSearch, IconChevronL, IconChevronR, IconPlus } from '../../components/Icons';
import SingleAssetUploadDialog from './SingleAssetUploadDialog';
import BulkImportDialog from './BulkImportDialog';
import EnvBanner from '../../components/EnvBanner';

const SORT_OPTIONS: { value: AssetSortBy; label: string }[] = [
  { value: 'CreatedAt', label: 'Created' },
  { value: 'UpdatedAt', label: 'Updated' },
  { value: 'Name', label: 'Name' },
  { value: 'Size', label: 'Size' }
];

export default function AssetManager() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [assetType, setAssetType] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedAssetPackId, setSelectedAssetPackId] = useState<string>('');
  const [sortBy, setSortBy] = useState<AssetSortBy>('CreatedAt');
  const [sortDescending, setSortDescending] = useState(true);
  const [strict, setStrict] = useState(false);
  const [includePrivate, setIncludePrivate] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = Number(localStorage.getItem(ASSET_PAGE_SIZE_STORAGE_KEY));
    return (ASSET_PAGE_SIZE_OPTIONS as readonly number[]).includes(stored)
      ? stored
      : ASSET_PAGE_SIZE_DEFAULT;
  });

  const [results, setResults] = useState<AssetSearchMatch[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availablePacks, setAvailablePacks] = useState<AssetPackDto[]>([]);

  const [selectedAsset, setSelectedAsset] = useState<AssetDto | null>(null);
  const [selectedDownloadUrl, setSelectedDownloadUrl] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showSingleUpload, setShowSingleUpload] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    getAvailableAssetTypes(includePrivate).then(setAvailableTypes).catch(() => setAvailableTypes([]));
    getAvailableTags(includePrivate).then(setAvailableTags).catch(() => setAvailableTags([]));
  }, [includePrivate]);

  useEffect(() => {
    getAssetPacks(1, 100).then(setAvailablePacks).catch(() => setAvailablePacks([]));
  }, [refreshKey]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem(ASSET_PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, assetType, selectedTags, selectedAssetPackId, sortBy, sortDescending, strict, includePrivate, pageSize]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [debouncedSearch, assetType, selectedTags, selectedAssetPackId, sortBy, sortDescending, strict, includePrivate, page, pageSize, refreshKey]);

  useEffect(() => {
    const params: SearchAssetsParams = {
      page,
      pageSize,
      sortBy,
      sortDescending,
      strict,
      includePrivate
    };
    if (debouncedSearch) params.searchTerm = debouncedSearch;
    if (assetType) params.assetType = assetType;
    if (selectedTags.length > 0) params.tags = selectedTags;
    if (selectedAssetPackId) params.assetPackId = selectedAssetPackId;

    setLoading(true);
    setError(null);
    searchAssets(params)
      .then((result) => {
        setResults(result.assets);
        setTotalCount(result.totalCount);
        setTotalPages(result.totalPages);
      })
      .catch(() => setError('Failed to load assets'))
      .finally(() => setLoading(false));
  }, [debouncedSearch, assetType, selectedTags, selectedAssetPackId, sortBy, sortDescending, strict, includePrivate, page, pageSize, refreshKey]);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  }, []);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allOnPageSelected = results.length > 0 && results.every((m) => selectedIds.has(m.asset.id));

  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const pageIds = results.map((m) => m.asset.id);
      const everySelected = pageIds.length > 0 && pageIds.every((id) => next.has(id));
      if (everySelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [results]);

  const handleBulkDeleted = useCallback((result: BulkDeleteResult) => {
    const deletedSet = new Set(result.deletedIds);
    if (deletedSet.size === 0) return;
    setResults((prev) => prev.filter((m) => !deletedSet.has(m.asset.id)));
    setTotalCount((c) => Math.max(0, c - deletedSet.size));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      deletedSet.forEach((id) => next.delete(id));
      return next;
    });
    if (selectedAsset && deletedSet.has(selectedAsset.id)) {
      setSelectedAsset(null);
      setSelectedDownloadUrl(null);
    }
  }, [selectedAsset]);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <EnvBanner />
      <div className="flex items-center justify-between border-b border-neutral-800 px-6 py-4">
        <div>
          <h1 className="text-2xl font-semibold">Asset Manager</h1>
          <p className="mt-1 text-sm text-neutral-400">
            {totalCount} {totalCount === 1 ? 'asset' : 'assets'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowSingleUpload(true)}
            className="flex items-center gap-2 rounded border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm hover:bg-neutral-800"
          >
            <IconPlus size={14} /> Upload single
          </button>
          <button
            type="button"
            onClick={() => setShowBulkImport(true)}
            className="flex items-center gap-2 rounded border border-blue-800 bg-blue-900/30 px-3 py-1.5 text-sm text-blue-200 hover:bg-blue-900/50"
          >
            <IconPlus size={14} /> Bulk import
          </button>
        </div>
      </div>
      {showSingleUpload && (
        <SingleAssetUploadDialog
          onCancel={() => setShowSingleUpload(false)}
          onUploaded={() => {
            setShowSingleUpload(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
      {showBulkImport && (
        <BulkImportDialog
          onCancel={() => setShowBulkImport(false)}
          onDone={() => {
            setShowBulkImport(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      <div className="flex">
        <main className="flex-1 px-6 py-4">
          <Toolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            assetType={assetType}
            onAssetTypeChange={setAssetType}
            availableTypes={availableTypes}
            availableTags={availableTags}
            selectedTags={selectedTags}
            onToggleTag={toggleTag}
            availablePacks={availablePacks}
            selectedAssetPackId={selectedAssetPackId}
            onAssetPackChange={setSelectedAssetPackId}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortDescending={sortDescending}
            onToggleSortDirection={() => setSortDescending((d) => !d)}
            strict={strict}
            onToggleStrict={() => setStrict((s) => !s)}
            includePrivate={includePrivate}
            onToggleIncludePrivate={() => setIncludePrivate((p) => !p)}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
          />

          {selectedIds.size > 0 && (
            <BulkActionBar
              selectedCount={selectedIds.size}
              allOnPageSelected={allOnPageSelected}
              onToggleSelectPage={toggleSelectAllOnPage}
              onClear={() => setSelectedIds(new Set())}
              onDelete={() => setShowBulkDeleteModal(true)}
            />
          )}

          {error && (
            <div className="mt-4 rounded border border-red-800 bg-red-900/30 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}

          {loading ? (
            <div className="mt-12 text-center text-neutral-500">Loading…</div>
          ) : results.length === 0 ? (
            <div className="mt-12 text-center text-neutral-500">No assets match those filters.</div>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {results.map((match) => (
                <AssetCard
                  key={match.asset.id}
                  asset={match.asset}
                  downloadUrl={match.downloadUrl ?? null}
                  selected={selectedAsset?.id === match.asset.id}
                  checked={selectedIds.has(match.asset.id)}
                  onToggleCheck={() => toggleSelected(match.asset.id)}
                  onSelect={() => {
                    setSelectedAsset(match.asset);
                    setSelectedDownloadUrl(match.downloadUrl ?? null);
                  }}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </main>

        {selectedAsset && (
          <AssetDetailPanel
            asset={selectedAsset}
            downloadUrl={selectedDownloadUrl}
            onClose={() => {
              setSelectedAsset(null);
              setSelectedDownloadUrl(null);
            }}
            onDeleted={(deletedId) => {
              setResults((prev) => prev.filter((m) => m.asset.id !== deletedId));
              setTotalCount((c) => Math.max(0, c - 1));
              setSelectedAsset(null);
              setSelectedDownloadUrl(null);
            }}
          />
        )}
      </div>

      {showBulkDeleteModal && (
        <BulkDeleteConfirmModal
          assets={results.filter((m) => selectedIds.has(m.asset.id)).map((m) => m.asset)}
          onCancel={() => setShowBulkDeleteModal(false)}
          onDone={(result) => {
            handleBulkDeleted(result);
            setShowBulkDeleteModal(false);
          }}
        />
      )}
    </div>
  );
}

interface ToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  assetType: string;
  onAssetTypeChange: (value: string) => void;
  availableTypes: string[];
  availableTags: string[];
  selectedTags: string[];
  onToggleTag: (tag: string) => void;
  availablePacks: AssetPackDto[];
  selectedAssetPackId: string;
  onAssetPackChange: (value: string) => void;
  sortBy: AssetSortBy;
  onSortByChange: (value: AssetSortBy) => void;
  sortDescending: boolean;
  onToggleSortDirection: () => void;
  strict: boolean;
  onToggleStrict: () => void;
  includePrivate: boolean;
  onToggleIncludePrivate: () => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}

function Toolbar(props: ToolbarProps) {
  const [showTagsMenu, setShowTagsMenu] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <IconSearch size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          type="text"
          placeholder="Search name / description / tags"
          value={props.searchTerm}
          onChange={(e) => props.onSearchChange(e.target.value)}
          className="w-72 rounded border border-neutral-800 bg-neutral-900 pl-7 pr-3 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
        />
      </div>

      <select
        value={props.assetType}
        onChange={(e) => props.onAssetTypeChange(e.target.value)}
        className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm"
      >
        <option value="">All types</option>
        {props.availableTypes.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>

      <select
        value={props.selectedAssetPackId}
        onChange={(e) => props.onAssetPackChange(e.target.value)}
        className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm"
      >
        <option value="">All packs</option>
        {props.availablePacks.map((p) => (
          <option key={p.id} value={p.id}>{p.packName} ({p.assetCount})</option>
        ))}
      </select>

      <div className="relative">
        <button
          type="button"
          onClick={() => setShowTagsMenu((s) => !s)}
          className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm"
        >
          Tags {props.selectedTags.length > 0 && `(${props.selectedTags.length})`}
        </button>
        {showTagsMenu && (
          <div className="absolute z-10 mt-1 max-h-64 w-64 overflow-auto rounded border border-neutral-800 bg-neutral-900 p-2 shadow-lg">
            {props.availableTags.length === 0 ? (
              <div className="px-2 py-1 text-xs text-neutral-500">No tags available</div>
            ) : (
              props.availableTags.map((tag) => (
                <label key={tag} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-neutral-800">
                  <input
                    type="checkbox"
                    checked={props.selectedTags.includes(tag)}
                    onChange={() => props.onToggleTag(tag)}
                  />
                  {tag}
                </label>
              ))
            )}
          </div>
        )}
      </div>

      <select
        value={props.sortBy}
        onChange={(e) => props.onSortByChange(e.target.value as AssetSortBy)}
        className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm"
      >
        {SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>Sort: {opt.label}</option>
        ))}
      </select>

      <button
        type="button"
        onClick={props.onToggleSortDirection}
        className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm"
        title={props.sortDescending ? 'Descending' : 'Ascending'}
      >
        {props.sortDescending ? '↓' : '↑'}
      </button>

      <select
        value={props.pageSize}
        onChange={(e) => props.onPageSizeChange(Number(e.target.value))}
        className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm"
        title="Items per page"
      >
        {ASSET_PAGE_SIZE_OPTIONS.map((n) => (
          <option key={n} value={n}>{n} / page</option>
        ))}
      </select>

      {props.selectedTags.length > 0 && (
        <button
          type="button"
          onClick={() => props.selectedTags.forEach((t) => props.onToggleTag(t))}
          className="text-xs text-neutral-400 hover:text-neutral-200"
        >
          Clear tags
        </button>
      )}

      <div className="ml-auto flex items-center gap-3">
        <label
          className={`flex cursor-pointer items-center gap-1.5 rounded border px-2 py-1.5 text-xs ${
            props.strict ? 'border-blue-700 bg-blue-900/30 text-blue-100' : 'border-neutral-800 bg-neutral-900 text-neutral-400'
          }`}
          title="Require ALL criteria to match (no fuzzy scoring)"
        >
          <input type="checkbox" checked={props.strict} onChange={props.onToggleStrict} className="accent-blue-500" />
          Strict
        </label>
        <label
          className={`flex cursor-pointer items-center gap-1.5 rounded border px-2 py-1.5 text-xs ${
            props.includePrivate ? 'border-amber-700 bg-amber-900/30 text-amber-100' : 'border-neutral-800 bg-neutral-900 text-neutral-400'
          }`}
          title="Include private assets owned by other users (admin only)"
        >
          <input type="checkbox" checked={props.includePrivate} onChange={props.onToggleIncludePrivate} className="accent-amber-500" />
          Include Private
        </label>
      </div>
    </div>
  );
}

interface BulkActionBarProps {
  selectedCount: number;
  allOnPageSelected: boolean;
  onToggleSelectPage: () => void;
  onClear: () => void;
  onDelete: () => void;
}

function BulkActionBar({ selectedCount, allOnPageSelected, onToggleSelectPage, onClear, onDelete }: BulkActionBarProps) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 rounded border border-blue-800 bg-blue-900/20 px-3 py-2 text-sm">
      <span className="font-medium text-blue-100">{selectedCount} selected</span>
      <label className="flex cursor-pointer items-center gap-2 text-xs text-blue-100">
        <input
          type="checkbox"
          checked={allOnPageSelected}
          onChange={onToggleSelectPage}
          className="accent-blue-500"
        />
        Select all on page
      </label>
      <button
        type="button"
        onClick={onClear}
        className="text-xs text-neutral-300 hover:text-neutral-100"
      >
        Clear
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="ml-auto rounded border border-red-700 bg-red-700 px-3 py-1.5 text-sm text-white hover:bg-red-800"
      >
        Delete {selectedCount} selected
      </button>
    </div>
  );
}

interface AssetCardProps {
  asset: AssetDto;
  downloadUrl: string | null;
  selected: boolean;
  checked: boolean;
  onToggleCheck: () => void;
  onSelect: () => void;
}

function AssetCard({ asset, downloadUrl, selected, checked, onToggleCheck, onSelect }: AssetCardProps) {
  return (
    <div
      onClick={onSelect}
      className={`group relative flex cursor-pointer flex-col overflow-hidden rounded border text-left transition ${
        checked ? 'border-blue-500 ring-2 ring-blue-500' : selected ? 'border-blue-500' : 'border-neutral-800 hover:border-neutral-600'
      }`}
    >
      <label
        className="absolute left-1.5 top-1.5 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded bg-neutral-950/80 hover:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggleCheck}
          className="h-4 w-4 accent-blue-500"
          aria-label={`Select ${asset.name}`}
        />
      </label>
      <div className="relative flex aspect-square items-center justify-center bg-neutral-900">
        {downloadUrl && asset.assetType === 'audio' ? (
          <audio controls preload="none" src={downloadUrl} aria-label={`Play ${asset.name}`} className="w-full px-2" />
        ) : downloadUrl ? (
          <img src={downloadUrl} alt={asset.name} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-xs text-neutral-600">No preview</span>
        )}
      </div>
      <div className="px-2 py-2">
        <div className="truncate text-sm font-medium">{asset.name}</div>
        {asset.assetPackName && (
          <div className="mt-0.5 truncate text-xs italic text-neutral-500">in {asset.assetPackName}</div>
        )}
        <div className="mt-1 flex items-center justify-between text-xs text-neutral-500">
          <span className="rounded bg-neutral-800 px-1.5 py-0.5">{asset.assetType}</span>
          <span>{formatBytes(asset.fileSizeBytes)}</span>
        </div>
        {asset.tags.length > 0 && (
          <div className="mt-1 truncate text-xs text-neutral-600">{asset.tags.slice(0, 3).join(', ')}</div>
        )}
      </div>
    </div>
  );
}

interface AssetDetailPanelProps {
  asset: AssetDto;
  downloadUrl: string | null;
  onClose: () => void;
  onDeleted: (assetId: string) => void;
}

function AssetDetailPanel({ asset, downloadUrl, onClose, onDeleted }: AssetDetailPanelProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <aside className="hidden w-96 shrink-0 overflow-y-auto border-l border-neutral-800 bg-neutral-950 px-4 py-4 lg:block">
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-lg font-semibold">{asset.name}</h2>
        <button type="button" onClick={onClose} className="text-neutral-500 hover:text-neutral-200">×</button>
      </div>
      <div className="mt-3 flex items-center justify-center rounded bg-neutral-900 p-3" style={{ minHeight: 200 }}>
        {downloadUrl && asset.assetType === 'audio' ? (
          <audio controls preload="none" src={downloadUrl} aria-label={`Play ${asset.name}`} className="w-full" />
        ) : downloadUrl ? (
          <img src={downloadUrl} alt={asset.name} className="max-h-72 max-w-full object-contain" />
        ) : (
          <span className="text-xs text-neutral-600">Loading preview…</span>
        )}
      </div>
      <DetailRow label="Description" value={asset.description ?? '—'} />
      <DetailRow label="Type" value={asset.assetType} />
      <DetailRow label="Extension" value={asset.fileExtension} />
      <DetailRow label="Size" value={formatBytes(asset.fileSizeBytes)} />
      <DetailRow label="Visibility" value={asset.visibility} />
      <DetailRow label="Created" value={new Date(asset.createdAt).toLocaleString()} />
      <DetailRow label="Updated" value={new Date(asset.updatedAt).toLocaleString()} />
      <DetailRow label="Tags" value={asset.tags.length > 0 ? asset.tags.join(', ') : '—'} />
      <DetailRow label="Pack" value={asset.assetPackName ?? '—'} />
      <DetailRow label="Pack ID" value={asset.assetPackId ?? '—'} mono />
      <DetailRow label="Asset ID" value={asset.id} mono />
      {asset.typeInfo && (
        <div className="mt-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-neutral-500">Type info</div>
          <pre className="overflow-auto rounded bg-neutral-900 p-2 text-xs">{JSON.stringify(asset.typeInfo, null, 2)}</pre>
        </div>
      )}
      <button
        type="button"
        onClick={() => setShowDeleteModal(true)}
        className="mt-6 w-full rounded border border-red-800 bg-red-900/20 px-3 py-2 text-sm text-red-300 hover:bg-red-900/40"
      >
        Delete asset
      </button>
      {showDeleteModal && (
        <DeleteConfirmModal
          asset={asset}
          onCancel={() => setShowDeleteModal(false)}
          onConfirmed={() => {
            setShowDeleteModal(false);
            onDeleted(asset.id);
          }}
        />
      )}
    </aside>
  );
}

function DeleteConfirmModal({
  asset,
  onCancel,
  onConfirmed
}: {
  asset: AssetDto;
  onCancel: () => void;
  onConfirmed: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = typed.trim() === asset.name;

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteAsset(asset.id);
      onConfirmed();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: unknown } };
      const status = axiosErr?.response?.status;
      const body = axiosErr?.response?.data;
      const serverMessage = typeof body === 'string' ? body : (body as { message?: string })?.message;
      setError(serverMessage ? `Delete failed (${status}): ${serverMessage}` : `Delete failed${status ? ` (${status})` : ''}.`);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div
        className="w-full max-w-md rounded border border-neutral-800 bg-neutral-950 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold">Delete asset</h3>
        <p className="mt-2 text-sm text-neutral-400">
          This will permanently remove the blob and the database row. Type the asset name to confirm:
        </p>
        <div className="mt-3 rounded bg-neutral-900 px-3 py-2 font-mono text-sm">{asset.name}</div>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          placeholder="Type asset name…"
          className="mt-3 w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm focus:border-neutral-600 focus:outline-none"
        />
        {error && <div className="mt-2 text-sm text-red-400">{error}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded border border-neutral-800 px-3 py-1.5 text-sm hover:bg-neutral-900"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!matches || busy}
            className="rounded border border-red-700 bg-red-700 px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

const BULK_DELETE_PREVIEW_COUNT = 10;
const BULK_DELETE_CONFIRM_WORD = 'DELETE';

function BulkDeleteConfirmModal({
  assets,
  onCancel,
  onDone
}: {
  assets: AssetDto[];
  onCancel: () => void;
  onDone: (result: BulkDeleteResult) => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState<BulkDeleteResult | null>(null);
  const matches = typed === BULK_DELETE_CONFIRM_WORD;
  const previewAssets = assets.slice(0, BULK_DELETE_PREVIEW_COUNT);
  const remaining = assets.length - previewAssets.length;

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await bulkDeleteAssets(assets.map((a) => a.id));
      const hadIssues = result.failures.length > 0 || result.notFoundIds.length > 0;
      if (hadIssues) {
        setPartial(result);
        setBusy(false);
      } else {
        onDone(result);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: unknown } };
      const status = axiosErr?.response?.status;
      const body = axiosErr?.response?.data;
      const serverMessage = typeof body === 'string' ? body : (body as { message?: string })?.message;
      setError(serverMessage ? `Delete failed (${status}): ${serverMessage}` : `Delete failed${status ? ` (${status})` : ''}.`);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div
        className="w-full max-w-md rounded border border-neutral-800 bg-neutral-950 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold">Delete {assets.length} {assets.length === 1 ? 'asset' : 'assets'}</h3>

        {partial ? (
          <>
            <p className="mt-2 text-sm text-neutral-400">
              Deleted {partial.deletedIds.length} of {assets.length}.
            </p>
            {partial.notFoundIds.length > 0 && (
              <p className="mt-1 text-sm text-amber-300">
                Not found: {partial.notFoundIds.length}
              </p>
            )}
            {partial.failures.length > 0 && (
              <div className="mt-2 max-h-40 overflow-auto rounded border border-red-900 bg-red-950/40 p-2 text-xs text-red-200">
                {partial.failures.map((f) => (
                  <div key={f.assetId} className="break-all">
                    <span className="font-mono">{f.assetId}</span>: {f.error}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => onDone(partial)}
                className="rounded border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm hover:bg-neutral-800"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-neutral-400">
              This will permanently remove the blobs and database rows. Type <span className="font-mono">{BULK_DELETE_CONFIRM_WORD}</span> to confirm.
            </p>
            <div className="mt-3 max-h-40 overflow-auto rounded bg-neutral-900 px-3 py-2 font-mono text-xs">
              {previewAssets.map((a) => (
                <div key={a.id} className="truncate">{a.name}</div>
              ))}
              {remaining > 0 && (
                <div className="mt-1 text-neutral-500">…and {remaining} more</div>
              )}
            </div>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              placeholder={`Type ${BULK_DELETE_CONFIRM_WORD}…`}
              className="mt-3 w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm focus:border-neutral-600 focus:outline-none"
            />
            {error && <div className="mt-2 text-sm text-red-400">{error}</div>}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="rounded border border-neutral-800 px-3 py-1.5 text-sm hover:bg-neutral-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!matches || busy}
                className="rounded border border-red-700 bg-red-700 px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? 'Deleting…' : `Delete ${assets.length} permanently`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="mt-3">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
      <div className={`text-sm ${mono ? 'break-all font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}

function Pagination({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (p: number) => void }) {
  return (
    <div className="mt-6 flex items-center justify-center gap-2 text-sm text-neutral-400">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="rounded border border-neutral-800 p-1.5 disabled:opacity-40"
      >
        <IconChevronL size={14} />
      </button>
      <span>Page {page} of {totalPages}</span>
      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className="rounded border border-neutral-800 p-1.5 disabled:opacity-40"
      >
        <IconChevronR size={14} />
      </button>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
